'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface BuiltForAISectionProps {
  isLoading?: boolean;
}

const patterns = [
  {
    title: 'Copy-pasted, not abstracted',
    description:
      'Near-duplicate functions: the same logic pasted in five places instead of one shared helper.',
  },
  {
    title: 'Future-proofing nobody asked for',
    description:
      'Exported helpers and symmetrical CRUD that nothing calls, flagged before they pile up as dead weight.',
  },
  {
    title: 'Tests that test the mock',
    description:
      'Assertions that check a mock returned what you told it to, not that the code actually works.',
  },
  {
    title: "Assertions that can't fail",
    description:
      'Weak checks that pass on almost any value, and flaky tests gated on sleeps and unseeded random.',
  },
  {
    title: 'Confident-but-wrong error handling',
    description:
      'Errors checked then ignored, swallowed in empty catches, or shadowed by an inner declaration.',
  },
  {
    title: 'Performance traps at scale',
    description:
      'N+1 I/O in loops, O(n²) string building, and maps used as caches that grow until they OOM.',
  },
];

export function BuiltForAISection({
  isLoading = false,
}: BuiltForAISectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1200px]">
          <Skeleton className="mx-auto mb-8 h-10 w-[55%]" />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-36 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        <h2
          className="text-gradient-hero mb-4 text-center text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          Built for how AI writes code.
        </h2>
        <p
          className="text-foreground/70 mb-10 text-center text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          Agents fail in patterns. These aren't slogans. They're checks
          Mindrealm runs for the mistakes agents make confidently.
        </p>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {patterns.map((pattern) => (
            <div
              key={pattern.title}
              className="rounded-xl p-6"
              style={{
                background: 'transparent',
                border: `1px solid ${theme.box.outline.border[isDark ? 'dark' : 'light']}`,
              }}
            >
              <h3
                className="mb-2 text-lg font-semibold md:text-xl"
                style={{ fontFamily: theme.fonts.display }}
              >
                {pattern.title}
              </h3>
              <p
                className="text-foreground/70 text-sm md:text-base"
                style={{ fontFamily: theme.fonts.body }}
              >
                {pattern.description}
              </p>
            </div>
          ))}
        </div>

        <p
          className="pt-10 text-center text-2xl font-semibold md:text-3xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          Your CI is green. Your confidence is false.
        </p>
      </div>
    </section>
  );
}
